import {
  buildMetricObservation,
  type MetricObservation,
  type MetricTagName,
} from './metrics'
import type { CanonicalIdPaths } from './redaction'
import { rumCorrelationIdPaths } from './rum-correlation'
import type { ObservabilityContext } from './types'

/**
 * Preserve only the validated metric envelope's reviewed ID paths. A generic
 * RUM action cannot opt out of redaction by merely naming a field `orgId`.
 * Unknown caller request IDs and their copies keep the same token filtering
 * used by the server envelope. Environment secrets are still scrubbed later.
 */
export function metricRumIdPaths(event: {
  type: string
  action?: { target?: { name?: string } }
  vital?: { name?: string; type?: string }
  context?: Record<string, unknown>
}): CanonicalIdPaths | undefined {
  try {
    if (event.context?.schema_version !== 1) {
      return
    }
    const metric = event.context.metric as
      | MetricObservation['metric']
      | undefined
    if (
      !metric ||
      typeof metric !== 'object' ||
      !metric.tags ||
      typeof metric.tags !== 'object'
    ) {
      return
    }
    const isMetricAction =
      event.type === 'action' &&
      event.action?.target?.name === 'telemetry.metric'
    const isJourneyVital =
      event.type === 'vital' &&
      event.vital?.type === 'duration' &&
      typeof event.vital.name === 'string' &&
      event.vital.name === metric.tags.operation &&
      metric.name === 'brew.journey.duration' &&
      metric.type === 'distribution' &&
      metric.unit === 'millisecond'
    if (!isMetricAction && !isJourneyVital) {
      return
    }
    const original = event.context.context as ObservabilityContext | undefined
    const validated = buildMetricObservation(
      {
        name: metric.name,
        type: metric.type,
        unit: metric.unit,
        tags: Object.keys(metric.tags) as Array<MetricTagName>,
      },
      metric.value,
      { tags: metric.tags, context: original }
    )
    if (!validated?.context) {
      return
    }
    const paths = rumCorrelationIdPaths(validated.context)
    return paths ? { context: paths } : undefined
  } catch {
    return undefined
  }
}
